import { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Share2, 
  X, 
  Twitter, 
  Facebook, 
  Linkedin,
  MessageCircle,
  Link2,
  Check,
  Send
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const MOOD_EMOJIS = {
  very_happy: "😄",
  happy: "🙂",
  neutral: "😐",
  sad: "😔",
  very_sad: "😢",
  angry: "😡",
  anxious: "😰",
  tired: "😴",
  in_love: "😍",
  sick: "🤒",
  surprised: "😲",
  relaxed: "😌",
  sporty: "💪",
  forgiven: "🙏",
};

const MOOD_LABELS = {
  very_happy: "muy feliz",
  happy: "bien",
  neutral: "neutral",
  sad: "triste",
  very_sad: "muy triste",
  angry: "enojado/a",
  anxious: "ansioso/a",
  tired: "cansado/a",
  in_love: "enamorado/a",
  sick: "enfermo/a",
  surprised: "sorprendido/a",
  relaxed: "relajado/a",
  sporty: "deportista",
  forgiven: "perdonado/a",
};

const MOOD_HASHTAGS = {
  very_happy: "#FelizDia #BuenasVibras",
  happy: "#BuenDia #Positivo",
  neutral: "#MiDia #Reflexion",
  sad: "#Sentimientos #EstaOk",
  very_sad: "#SaludMental #Apoyo",
  angry: "#Emociones #Desahogo",
  anxious: "#Ansiedad #Respira",
  tired: "#Descanso #Autocuidado",
  in_love: "#Amor #Enamorado #BuenasVibras",
  sick: "#Recuperacion #SaludPrimero",
  surprised: "#Sorpresa #QueLocura",
  relaxed: "#Relax #Paz #Tranquilidad",
  sporty: "#Deporte #Fitness #Energia",
  forgiven: "#Perdon #Paz #NuevoComienzo",
};

export const ShareMood = ({ mood, note, date, onClose }) => {
  const [copied, setCopied] = useState(false);
  
  const emoji = MOOD_EMOJIS[mood] || "😐";
  const label = MOOD_LABELS[mood] || mood;
  const hashtags = MOOD_HASHTAGS[mood] || "#MoodPunk";
  
  // Get greeting based on time of day
  const getTimeGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 12) {
      return "Buenos días"; // 6am - 12pm
    } else if (hour >= 12 && hour < 19) {
      return "Buenas tardes"; // 12pm - 7pm
    } else {
      return "Buenas noches"; // 7pm - 6am
    }
  };
  
  const getTimeEmoji = () => {
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 12) {
      return "☀️"; // Morning sun
    } else if (hour >= 12 && hour < 19) {
      return "🌤️"; // Afternoon
    } else {
      return "🌙"; // Night moon
    }
  };
  
  const timeGreeting = getTimeGreeting();
  const timeEmoji = getTimeEmoji();
  
  // Generate share text with time-based greeting
  const shareText = note 
    ? `${timeEmoji} ¡${timeGreeting}! ${emoji} Me siento ${label}. "${note}" ${hashtags} #MoodPunk`
    : `${timeEmoji} ¡${timeGreeting}! ${emoji} Me siento ${label}. ${hashtags} #MoodPunk`;
  
  const shareUrl = "https://www.moodpunk.com"; // URL de la app
  
  // Share functions for each platform - work on both mobile and desktop
  const shareToTwitter = () => {
    try {
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
      window.open(url, "_blank", "noopener,noreferrer,width=600,height=400");
      toast.success("Abriendo Twitter...");
    } catch (err) {
      console.error("Twitter share error:", err);
      toast.error("No se pudo abrir Twitter");
    }
  };
  
  const shareToFacebook = () => {
    try {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
      window.open(url, "_blank", "noopener,noreferrer,width=600,height=400");
      toast.success("Abriendo Facebook...");
    } catch (err) {
      console.error("Facebook share error:", err);
      toast.error("No se pudo abrir Facebook");
    }
  };
  
  const shareToLinkedIn = () => {
    try {
      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
      window.open(url, "_blank", "noopener,noreferrer,width=600,height=400");
      toast.success("Abriendo LinkedIn...");
    } catch (err) {
      console.error("LinkedIn share error:", err);
      toast.error("No se pudo abrir LinkedIn");
    }
  };
  
  const shareToWhatsApp = () => {
    try {
      const url = `https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`;
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success("Abriendo WhatsApp...");
    } catch (err) {
      console.error("WhatsApp share error:", err);
      toast.error("No se pudo abrir WhatsApp");
    }
  };
  
  const shareToTelegram = () => {
    try {
      const url = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success("Abriendo Telegram...");
    } catch (err) {
      console.error("Telegram share error:", err);
      toast.error("No se pudo abrir Telegram");
    }
  };
  
  const copyToClipboard = async () => {
    const textToCopy = shareText + " " + shareUrl;
    
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        toast.success("¡Copiado al portapapeles!");
        setTimeout(() => setCopied(false), 2000);
        return;
      }
      
      // Fallback for non-secure contexts (HTTP)
      const textArea = document.createElement("textarea");
      textArea.value = textToCopy;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);
      
      if (successful) {
        setCopied(true);
        toast.success("¡Copiado al portapapeles!");
        setTimeout(() => setCopied(false), 2000);
      } else {
        throw new Error("Copy failed");
      }
    } catch (err) {
      console.error("Copy error:", err);
      toast.error("No se pudo copiar. Selecciona el texto manualmente.");
    }
  };
  
  // Use native share if available (works better on mobile)
  const nativeShare = async () => {
    try {
      await navigator.share({
        title: "Mi estado de ánimo - MoodPunk",
        text: shareText,
        url: shareUrl,
      });
      toast.success("¡Compartido!");
    } catch (err) {
      // User cancelled or error - silently handle
      if (err.name !== "AbortError") {
        console.log("Native share not available, using buttons");
      }
    }
  };
  
  // Check if native share is supported
  const hasNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  
  const socialButtons = [
    { 
      name: "Twitter / X", 
      icon: Twitter, 
      onClick: shareToTwitter, 
      color: "hover:bg-[#1DA1F2]/20 hover:border-[#1DA1F2]/50 hover:text-[#1DA1F2]",
      testId: "share-twitter"
    },
    { 
      name: "Facebook", 
      icon: Facebook, 
      onClick: shareToFacebook, 
      color: "hover:bg-[#4267B2]/20 hover:border-[#4267B2]/50 hover:text-[#4267B2]",
      testId: "share-facebook"
    },
    { 
      name: "WhatsApp", 
      icon: MessageCircle, 
      onClick: shareToWhatsApp, 
      color: "hover:bg-[#25D366]/20 hover:border-[#25D366]/50 hover:text-[#25D366]",
      testId: "share-whatsapp"
    },
    { 
      name: "Telegram", 
      icon: Send, 
      onClick: shareToTelegram, 
      color: "hover:bg-[#0088cc]/20 hover:border-[#0088cc]/50 hover:text-[#0088cc]",
      testId: "share-telegram"
    },
    { 
      name: "LinkedIn", 
      icon: Linkedin, 
      onClick: shareToLinkedIn, 
      color: "hover:bg-[#0077B5]/20 hover:border-[#0077B5]/50 hover:text-[#0077B5]",
      testId: "share-linkedin"
    },
  ];

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-void-paper border border-white/10 rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-void-paper z-10">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-neon-cyan" />
            <h3 className="font-rajdhani font-bold text-lg text-white uppercase tracking-wide">
              Compartir
            </h3>
          </div>
          <button 
            onClick={onClose}
            data-testid="close-share-modal"
            aria-label="Cerrar"
            className="text-slate-400 hover:text-white transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Preview Card */}
        <div className="p-4">
          <div className="bg-gradient-to-br from-neon-cyan/10 to-neon-purple/10 border border-white/10 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-5xl">{emoji}</span>
              <div>
                <p className="text-xs text-slate-400 font-rajdhani mb-1">
                  {timeEmoji} ¡{timeGreeting}!
                </p>
                <p className="font-orbitron text-white text-lg">
                  Me siento <span className="text-neon-cyan">{label}</span>
                </p>
                <p className="text-xs text-slate-500 font-rajdhani">
                  {date ? new Date(date).toLocaleDateString("es", { 
                    weekday: "long", 
                    day: "numeric", 
                    month: "long" 
                  }) : "Hoy"}
                </p>
              </div>
            </div>
            {note && (
              <p className="text-sm text-slate-300 font-inter italic border-t border-white/10 pt-3 mt-2">
                "{note}"
              </p>
            )}
            <p className="text-xs text-neon-purple mt-3 font-rajdhani">
              {hashtags} #MoodPunk
            </p>
          </div>

          {/* Native Share Button (Mobile) */}
          {hasNativeShare && (
            <Button
              onClick={nativeShare}
              data-testid="share-native"
              className="w-full mb-4 py-4 font-rajdhani font-bold uppercase tracking-widest bg-neon-cyan/20 hover:bg-neon-cyan/30 text-neon-cyan border border-neon-cyan/50 rounded-xl transition-all"
            >
              <Share2 className="w-5 h-5 mr-2" />
              Compartir
            </Button>
          )}

          {/* Social Buttons Grid */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            {socialButtons.map((btn) => (
              <button
                key={btn.name}
                onClick={btn.onClick}
                data-testid={btn.testId}
                className={`flex flex-col items-center gap-2 p-3 rounded-xl bg-white/5 border border-white/10 text-slate-400 transition-all ${btn.color}`}
              >
                <btn.icon className="w-6 h-6" />
                <span className="text-xs font-rajdhani">{btn.name}</span>
              </button>
            ))}
            
            {/* Copy Link */}
            <button
              onClick={copyToClipboard}
              data-testid="share-copy"
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/5 border border-white/10 text-slate-400 transition-all hover:bg-neon-purple/20 hover:border-neon-purple/50 hover:text-neon-purple"
            >
              {copied ? (
                <Check className="w-6 h-6 text-neon-green" />
              ) : (
                <Link2 className="w-6 h-6" />
              )}
              <span className="text-xs font-rajdhani">{copied ? "¡Copiado!" : "Copiar"}</span>
            </button>
          </div>

          {/* Preview Text */}
          <div className="bg-black/30 rounded-lg p-3">
            <p className="text-xs text-slate-500 font-rajdhani mb-1">Vista previa del texto:</p>
            <p className="text-sm text-slate-300 font-inter break-words">{shareText}</p>
            <p className="text-xs text-neon-cyan mt-2 break-all">{shareUrl}</p>
          </div>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

// Share Button Component (to use in mood entries)
export const ShareButton = ({ mood, note, date }) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        data-testid="open-share-modal"
        className="p-2 rounded-lg bg-white/5 hover:bg-neon-cyan/10 border border-transparent hover:border-neon-cyan/30 text-slate-400 hover:text-neon-cyan transition-all"
        title="Compartir en redes sociales"
      >
        <Share2 className="w-4 h-4" />
      </button>
      
      <AnimatePresence>
        {showModal && (
          <ShareMood
            mood={mood}
            note={note}
            date={date}
            onClose={() => setShowModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};
